import React, { useCallback } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableRow } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';

import { useTranslation } from 'react-i18next';
import PaginatedTableFooter from 'components/List/PaginatedTableFooter';
import SortableTableHead from 'components/List/SortableTableHead';
import Loading from 'components/Loading';
import { ListableEntity, UserRole } from 'types';
import TableCellWithConfigurableStyle, { EntityColumn } from './TableCellWithConfigurableStyle';

const useStyles = makeStyles({
  tableContainer: {
    padding: 15,
  },
  paddedContainer: {
    padding: 15,
    maxWidth: '80%',
    margin: 'auto',
  },
  table: {
    minWidth: '20rem',
    '& *': {
      fontFamily: '"Open Sans", "Helvetica", "Arial", sans-serif',
    },
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-start',
    '& > *:not(:last-child)': {
      marginRight: '3px',
    },
    '& > *:not(:first-child)': {
      marginLeft: '3px',
    },
    '& button': {
      padding: '0.5rem',
      minWidth: 'initial',
    },
    '& a': {
      minWidth: 'initial',
    },
  },
  loadingCell: {
    borderBottom: '0px',
    paddingTop: 50,
  },
  withCursor: {
    cursor: 'pointer',
  },
  emptyTable: {
    fontSize: '14px',
    lineHeight: '16px',
    textAlign: 'center',
    paddingTop: '50px',
    border: '0px',
  },
});

export type AvailableListAction = {
  title: string;
  onClick: () => void;
  action: JSX.Element;
  disabled?: boolean;
};

type GenericListProps<T extends ListableEntity> = {
  columns: EntityColumn[];
  userRole?: UserRole;
  entities?: T[] | null;
  total: number;
  generateAvailableActions?: (entity: T, userRole?: UserRole) => AvailableListAction[];
  includeCancelledLabel?: string;
  detailsLink: string;
  biggerSideMargins?: boolean;
  noHiddenHour?: boolean;
};

type GenericListRow<T extends ListableEntity> = {
  hasAction: boolean;
  columns: EntityColumn[];
  userRole?: UserRole;
  entity: any | null;
  generateAvailableActions?: (entity: T, userRole?: UserRole) => AvailableListAction[];
  detailsLink: string;
  noHiddenHour?: boolean;
};

const EntityTableRow: React.FC<GenericListRow<any>> = ({
  entity,
  hasAction,
  columns,
  generateAvailableActions,
  userRole,
  detailsLink,
  noHiddenHour,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const onClick = useCallback(() => {
    history.push(`${detailsLink}/${entity.id}`, { referer: history.location.pathname });
  }, [history, detailsLink, entity.id]);
  return (
    <TableRow
      data-qa-id="table-section-row"
      hover={hasAction}
      className={classNames({ [classes.withCursor]: hasAction })}
    >
      {columns.map(c => (
        <TableCellWithConfigurableStyle
          entity={entity}
          column={c}
          onClick={hasAction ? onClick : undefined}
          key={`${entity.id}-${c.headerTranslationKey}`}
          availableActions={generateAvailableActions?.(entity, userRole)}
          detailsLink={detailsLink}
          noHiddenHour={noHiddenHour}
        />
      ))}
    </TableRow>
  );
};

function GenericList<T extends ListableEntity>({
  columns,
  entities,
  total,
  biggerSideMargins,
  ...rest
}: GenericListProps<T>) {
  const { t } = useTranslation();
  const classes = useStyles();
  const hasAction = columns.some(c => c.type === 'action');

  return (
    <>
      <TableContainer className={biggerSideMargins ? classes.paddedContainer : classes.tableContainer}>
        <Table className={classes.table} data-qa-id="entities-table">
          {!entities ? (
            <TableBody>
              <TableRow>
                <TableCell className={classes.loadingCell}>
                  <Loading />
                </TableCell>
              </TableRow>
            </TableBody>
          ) : (
            <>
              {entities.length > 0 ? (
                <>
                  <SortableTableHead columns={columns} />
                  <TableBody>
                    {entities.map(entity => (
                      <EntityTableRow
                        key={entity.id}
                        hasAction={hasAction}
                        columns={columns}
                        entity={entity}
                        {...rest}
                      />
                    ))}
                  </TableBody>
                  <PaginatedTableFooter total={total} />
                </>
              ) : (
                <TableBody>
                  <TableRow>
                    <TableCell className={classes.emptyTable}>
                      <div>{t('emptyTable')}</div>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </>
          )}
        </Table>
      </TableContainer>
    </>
  );
}
export default React.memo(GenericList) as typeof GenericList;
